import classNames from "classnames";
import {
    Avatar,
    type AvatarShape,
    type AvatarSize,
} from "@design-system/Avatar/Avatar";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import { KickLogo } from "@design-system/KickLogo/KickLogo";
import { avatarPaintProps, toAvatarGroupSize } from "./AvatarPlayground.lib";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import { parseAvatarAnatomyVariantId } from "./componentAnatomyAvatarPaints";
import { diamondOuterSide } from "./componentAnatomyAvatarSizes";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import viewStyles from "./ComponentAnatomyView.module.scss";
import styles from "./ComponentAnatomySchematicAvatar.module.scss";

/** The playground's size, so the x-ray draws the same avatar as the live stage. */
export interface AvatarSchematicContent {
    size: AvatarSize;
}

const stackPeople = [
    { id: "maya", name: "Maya Patel", color: "#FADEC4" },
    { id: "david", name: "David Cohen", color: "#BDF0D5" },
    { id: "anna", name: "Anna Lee", color: "#D9CCFF" },
    { id: "ben", name: "Ben Carter" },
    { id: "thea", name: "Thea Morgan" },
];

const stackVisibleCount = 3;

/** Mirrors `--avatar-radius` on a single avatar, where a diamond's fill is the whole outer diamond. */
const cornerRadius = (shape: AvatarShape) => {
    switch (shape) {
        case "circle":
            return "50%";
        case "square":
            return "var(--radius-xs)";
        case "diamond":
            return "var(--radius-xs)";
    }
};

/**
 * Real `Avatar` and `AvatarGroup` instances with transparent hover targets
 * laid over each part, so the legend highlight points at live pixels.
 */
export const AvatarSchematic = ({
    bindings,
    highlight,
    variantId,
    content = { size: "md" },
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    variantId: string;
    content?: AvatarSchematicContent;
}) => {
    const read = bindingReader(bindings);
    const { shape, variant, tone } = parseAvatarAnatomyVariantId(variantId);
    const boxSize = read("background", "width") ?? "20px";
    const overlap = read("seam", "overlap") ?? "0px";
    const chipGap = read("countChip", "gap");
    const radius = cornerRadius(shape);
    const stackWidth = `calc(${stackVisibleCount} * ${boxSize} - ${stackVisibleCount - 1} * ${overlap})`;
    const { size } = content;
    const groupSize = toAvatarGroupSize(size);
    const isDiamond = shape === "diamond";
    const diamondStyle = {
        borderRadius: radius,
        inset: 0,
        width: diamondOuterSide(boxSize),
        height: diamondOuterSide(boxSize),
        margin: "auto",
        transform: "rotate(45deg)",
    };
    const edgeStyle = isDiamond ? diamondStyle : { borderRadius: radius };

    return (
        <span className={classNames(viewStyles.schematic, styles.schematic)}>
            <span className={styles.cell}>
                <Avatar
                    name="Maya Patel"
                    size={size}
                    shape={shape}
                    {...avatarPaintProps({ variant, tone, color: undefined })}
                />
                <Part
                    partId="background"
                    highlight={highlight}
                    className={styles.overlay}
                    style={isDiamond ? undefined : { borderRadius: radius }}
                />
                {isDiamond && (
                    <Part
                        partId="fill"
                        highlight={highlight}
                        className={styles.overlay}
                        style={diamondStyle}
                    />
                )}
                {variant === "outline" && (
                    <Part
                        partId="border"
                        highlight={highlight}
                        className={styles.overlay}
                        style={edgeStyle}
                    />
                )}
                <Part
                    partId="label"
                    highlight={highlight}
                    className={styles.labelOverlay}
                />
            </span>
            {shape !== "diamond" && (
                <span className={styles.cell}>
                    <Avatar
                        name="Kick"
                        size={size}
                        shape={shape}
                        fallback={<KickLogo variant="icon" shape={shape} />}
                        {...avatarPaintProps({
                            variant,
                            tone,
                            color: undefined,
                        })}
                    />
                    <Part
                        partId="glyph"
                        highlight={highlight}
                        className={styles.overlay}
                        style={{ borderRadius: radius }}
                    />
                </span>
            )}
            {groupSize !== undefined && chipGap !== undefined && (
                <span className={styles.cell}>
                    <AvatarGroup
                        items={stackPeople}
                        getKey={(person) => person.id}
                        getLabel={(person) => person.name}
                        renderAvatar={(person) => (
                            <Avatar
                                name={person.name}
                                shape={shape}
                                {...avatarPaintProps({
                                    variant,
                                    tone,
                                    color: person.color,
                                })}
                            />
                        )}
                        max={stackVisibleCount}
                        size={groupSize}
                    />
                    <Part
                        partId="seam"
                        highlight={highlight}
                        className={styles.overlay}
                        style={{ insetInlineEnd: "auto", width: stackWidth }}
                    />
                    <Part
                        partId="countChip"
                        highlight={highlight}
                        className={styles.overlay}
                        style={{
                            insetInlineStart: `calc(${stackWidth} + ${chipGap})`,
                            borderRadius: read("countChip", "border-radius"),
                        }}
                    />
                </span>
            )}
        </span>
    );
};
